import { useState } from "react";
import { Star, ExternalLink, Quote, MessageSquarePlus } from "lucide-react";
import { REVIEWS_DATA, RESTAURANT } from "../data/site";
import { Reveal } from "./Reveal";

const Stars = ({ rating = 5, size = 16 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        className={n <= Math.round(rating) ? "fill-gold text-gold" : "text-gold/30"}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const { overallRating, totalReviews, reviews } = REVIEWS_DATA;
  const hasReviews = reviews && reviews.length > 0;
  const [visible, setVisible] = useState(6);

  return (
    <section id="reviews" data-testid="reviews-section" className="bg-cream py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <Reveal>
            <span className="font-heading text-gold-dark uppercase tracking-[0.3em] text-sm">
              — What Guests Say
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-charcoal">
              Google <span className="font-script text-burgundy text-5xl md:text-6xl">Reviews</span>
            </h2>
          </Reveal>

          {hasReviews && (
            <Reveal delay={0.1}>
              <div className="mt-6 inline-flex items-center gap-4 bg-white border border-beige rounded-2xl px-7 py-4 shadow-[0_4px_20px_rgba(122,28,34,0.05)]">
                <span className="font-heading text-4xl font-bold text-burgundy">
                  {overallRating?.toFixed(1)}
                </span>
                <div className="text-left">
                  <Stars rating={overallRating} size={18} />
                  <p className="font-body text-sm text-charcoal/60 mt-1">
                    {totalReviews} Google reviews
                  </p>
                </div>
              </div>
            </Reveal>
          )}
        </div>

        {hasReviews ? (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, visible).map((r, i) => (
                <Reveal
                  key={i}
                  delay={(i % 3) * 0.06}
                  className="bg-white rounded-3xl p-7 border border-beige shadow-[0_4px_20px_rgba(122,28,34,0.05)] hover:shadow-[0_14px_34px_rgba(122,28,34,0.12)] hover:-translate-y-1 transition-all duration-300 relative"
                >
                  <Quote className="absolute top-6 right-6 text-gold/25" size={34} />
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-burgundy text-cream flex items-center justify-center font-heading font-semibold">
                      {r.initials}
                    </div>
                    <div>
                      <p className="font-body font-semibold text-charcoal">{r.author}</p>
                      <p className="font-body text-xs text-charcoal/50">{r.date}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Stars rating={r.rating} />
                  </div>
                  <p className="mt-3 font-body text-charcoal/75 leading-relaxed">{r.text}</p>
                </Reveal>
              ))}
            </div>
            {visible < reviews.length && (
              <div className="text-center mt-10">
                <button
                  data-testid="load-more-reviews"
                  onClick={() => setVisible((v) => v + 6)}
                  className="border border-gold text-charcoal px-8 py-3.5 rounded-full font-body font-semibold tracking-wide hover:bg-gold hover:text-cream transition-all duration-300"
                >
                  View More Reviews
                </button>
              </div>
            )}
          </>
        ) : (
          <Reveal>
            <div
              data-testid="reviews-placeholder"
              className="max-w-2xl mx-auto text-center bg-white border border-dashed border-gold/50 rounded-3xl p-12"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-beige flex items-center justify-center text-burgundy mb-5">
                <MessageSquarePlus size={28} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-charcoal">
                Real reviews are on their way
              </h3>
              <p className="mt-3 font-body text-charcoal/65 leading-relaxed">
                We only show genuine Google reviews from real guests. Live reviews will appear
                here soon — in the meantime, read what people are saying on Google.
              </p>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.1} className="text-center mt-10">
          <a
            data-testid="read-google-reviews-btn"
            href={RESTAURANT.googleListingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-burgundy text-cream px-8 py-4 rounded-full font-body font-semibold tracking-wide hover:bg-burgundy-dark transition-all duration-300 hover:scale-[1.03]"
          >
            Read More Reviews on Google <ExternalLink size={17} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};
